/**
 * Display formatters used across feature screens. Centralized so the
 * same date/currency styles render consistently — no per-feature drift.
 */

import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';

export function formatDate(input: string | Date | undefined, fmt = 'd MMM yyyy'): string {
  if (!input) return '';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (Number.isNaN(date.getTime())) return '';
  return format(date, fmt);
}

export function formatDateTime(input: string | Date | undefined): string {
  return formatDate(input, "d MMM yyyy 'at' HH:mm");
}

/** Editorial relative date: "Today", "Yesterday", "3 days ago", or "12 May 2025". */
export function formatRelative(input: string | Date | undefined): string {
  if (!input) return '';
  const date = typeof input === 'string' ? parseISO(input) : input;
  if (Number.isNaN(date.getTime())) return '';
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  const diffDays = (Date.now() - date.getTime()) / 86_400_000;
  if (diffDays < 7) return formatDistanceToNow(date, { addSuffix: true });
  return format(date, 'd MMM yyyy');
}

/**
 * Currency formatter using Intl.NumberFormat. Defaults to ZAR — the
 * primary IP4 estates region; per-tenant override should pass `currency`.
 */
export function formatMoney(
  amount: number | string | undefined,
  options: { currency?: string; locale?: string } = {},
): string {
  if (amount === undefined || amount === null) return '';
  const value = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(value)) return '';
  return new Intl.NumberFormat(options.locale ?? 'en-ZA', {
    style: 'currency',
    currency: options.currency ?? 'ZAR',
    minimumFractionDigits: 2,
  }).format(value);
}

/** Display-friendly initials from a full name, falling back to email. */
export function initialsOf(name: string | undefined, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
    return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
  }
  if (email) return email.charAt(0).toUpperCase();
  return '?';
}

/** Truncate plain text mid-sentence with an ellipsis at character count. */
export function truncate(value: string | undefined, max: number): string {
  if (!value) return '';
  if (value.length <= max) return value;
  return value.slice(0, max - 1).trimEnd() + '…';
}
